import React, { useEffect, useState } from 'react';
import { ChevronLeft, Send, Star, Paperclip, Share2, ExternalLink } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';
interface RequestTicketPageProps {
  requestId: number;
  onBack: () => void;
}
interface Message {
  id: number;
  sender: 'user' | 'agent';
  text: string;
  timestamp: string;
  attachments?: string[];
}
export function RequestTicketPage({
  requestId,
  onBack
}: RequestTicketPageProps) {
  const [messageText, setMessageText] = useState('');
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Mock ticket data with 6-digit code
  const ticket = {
    id: requestId,
    ticketNumber: '#21321',
    requestCode: '762532',
    status: 'in-progress',
    project: {
      name: 'Project X',
      type: 'Apartment',
      baths: 2,
      beds: 3,
      price: '1,002,0000 EGP'
    }
  };
  // Mock agent data
  const agent = {
    name: 'Mohamed Hossam',
    position: 'Agent',
    avatar: 'https://cdn.magicpatterns.com/uploads/dmyzTWp4qVkkavD8xUDYZW/OIP.g0qYLq0G09unEJPan_GKvwHaHw.webp',
    rating: 5
  };
  // Mock messages
  const [messages, setMessages] = useState<Message[]>([{
    id: 1,
    sender: 'user',
    text: 'I want more information about this unit I want mo re information about this unit',
    timestamp: '10:30 AM'
  }, {
    id: 2,
    sender: 'agent',
    text: 'I want more information about this unit I want mo re information about this unit w ant mo re information about this unit ation about this unit',
    timestamp: '10:35 AM',
    attachments: ['Document.pdf']
  }, {
    id: 3,
    sender: 'user',
    text: 'Thanks about this unit w ant mo re information about this unit ation about this unit',
    timestamp: '10:40 AM'
  }]);
  const handleSendMessage = () => {
    if (messageText.trim()) {
      const newMessage: Message = {
        id: messages.length + 1,
        sender: 'user',
        text: messageText,
        timestamp: new Date().toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        })
      };
      setMessages([...messages, newMessage]);
      setMessageText('');
    }
  };
  const getStatusBadge = () => {
    const statusConfig = {
      'in-progress': {
        label: 'In Progress',
        bg: 'bg-blue-500',
        text: 'text-white'
      }
    };
    const config = statusConfig[ticket.status as keyof typeof statusConfig];
    return <span className={`${config.bg} ${config.text} text-xs font-semibold px-3 py-1 rounded-full`} data-id="element-3137">
        {config.label}
      </span>;
  };
  return <div className="min-h-screen bg-neutral-bg font-sans w-full flex flex-col" data-id="element-3138">
      <div className="w-full mx-auto bg-neutral-bg flex-1 flex flex-col relative" data-id="element-3139">
        {/* Header */}
        <div className="bg-primary px-4 py-4 safe-top flex items-center justify-between sticky top-0 z-10 shadow-sm" data-id="element-3140">
          <button onClick={onBack} className="p-2 -ml-2 text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-3141">
            <ChevronLeft className="w-6 h-6" data-id="element-3142" />
          </button>
          <h1 className="font-semibold text-base text-white" data-id="element-3143">
            Request Number {ticket.ticketNumber}
          </h1>
          <div className="w-10" data-id="element-3144"></div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto pb-32" data-id="element-3145">
          {/* Agent Info Card */}
          <div className="px-4 pt-4 pb-3" data-id="element-3146">
            <div className="bg-gradient-to-r from-teal-100 via-blue-50 to-purple-100 rounded-2xl p-4 shadow-sm" data-id="element-3147">
              <div className="flex items-center gap-3" data-id="element-3148">
                <div className="w-14 h-14 rounded-full border-3 border-accent overflow-hidden bg-white" data-id="element-3149">
                  <img src={agent.avatar} alt={agent.name} className="w-full h-full object-cover" data-id="element-3150" />
                </div>
                <div className="flex-1" data-id="element-3151">
                  <p className="text-xs font-medium text-primary mb-0.5" data-id="element-3152">
                    {agent.position}
                  </p>
                  <h3 className="text-base font-bold text-primary mb-1" data-id="element-3153">
                    {agent.name}
                  </h3>
                  <div className="flex gap-0.5" data-id="element-3154">
                    {[...Array(5)].map((_, i) => <Star key={i} className={`w-3 h-3 ${i < agent.rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'}`} data-id="element-3155" />)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Request Info Card */}
          <div className="px-4 pb-4" data-id="element-3156">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4" data-id="element-3157">
              <div className="flex items-center justify-between mb-3" data-id="element-3158">
                <p className="text-xs font-semibold text-secondary" data-id="element-3159">
                  Request No. #{ticket.requestCode}
                </p>
                {getStatusBadge()}
              </div>

              <h4 className="font-bold text-neutral-text mb-1" data-id="element-3160">
                {ticket.project.name}
              </h4>
              <div className="flex items-center gap-4 text-sm text-gray-600" data-id="element-3161">
                <span data-id="element-3162">{ticket.project.type}</span>
                <span data-id="element-3163">{ticket.project.baths} Bath</span>
                <span data-id="element-3164">{ticket.project.beds} Beds</span>
              </div>
              <p className="text-sm font-semibold text-neutral-text mt-2" data-id="element-3165">
                {ticket.project.price}
              </p>
            </div>
          </div>

          {/* Messages */}
          <div className="px-4 space-y-4 pb-4" data-id="element-3166">
            {messages.map(message => <div key={message.id} className={`flex gap-3 ${message.sender === 'user' ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-3167">
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-gray-300 flex-shrink-0 overflow-hidden" data-id="element-3168">
                  {message.sender === 'agent' ? <img src={agent.avatar} alt={agent.name} className="w-full h-full object-cover" data-id="element-3169" /> : <div className="w-full h-full bg-secondary/20 flex items-center justify-center" data-id="element-3170">
                      <span className="text-sm font-semibold text-secondary" data-id="element-3171">
                        AH
                      </span>
                    </div>}
                </div>

                {/* Message Content */}
                <div className={`flex-1 ${message.sender === 'user' ? 'items-end' : 'items-start'} flex flex-col`} data-id="element-3172">
                  <div className={`max-w-[85%] rounded-2xl p-4 ${message.sender === 'agent' ? 'bg-gradient-to-r from-teal-100 via-blue-50 to-purple-100' : 'bg-gray-100'}`} data-id="element-3173">
                    {message.sender === 'agent' && <p className="text-xs font-semibold text-primary mb-1" data-id="element-3174">
                        {agent.position}
                      </p>}
                    {message.sender === 'agent' && <p className="text-sm font-bold text-primary mb-2" data-id="element-3175">
                        {agent.name}
                      </p>}
                    {message.sender === 'user' && <p className="text-sm font-bold text-neutral-text mb-2" data-id="element-3176">
                        Ahmed Hussien
                      </p>}
                    <p className="text-sm text-gray-700 leading-relaxed" data-id="element-3177">
                      {message.text}
                    </p>

                    {/* Attachments */}
                    {message.attachments && message.attachments.length > 0 && <div className="mt-3 flex gap-2" data-id="element-3178">
                        <button className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-3179">
                          <ExternalLink className="w-4 h-4" data-id="element-3180" />
                          Open
                        </button>
                        <button className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-3181">
                          <Share2 className="w-4 h-4" data-id="element-3182" />
                          Share
                        </button>
                      </div>}
                  </div>
                  <p className="text-xs text-gray-400 mt-1 px-2" data-id="element-3183">
                    {message.timestamp}
                  </p>
                </div>
              </div>)}
          </div>

          {/* Rate Services Section */}
          {showRating && <div className="px-4 pb-4" data-id="element-3184">
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 text-center" data-id="element-3185">
                <h4 className="text-base font-semibold text-neutral-text mb-4" data-id="element-3186">
                  Rate the services
                </h4>
                <div className="flex justify-center gap-2" data-id="element-3187">
                  {[1, 2, 3, 4, 5].map(star => <button key={star} onClick={() => setRating(star)} className="transition-transform hover:scale-110" data-id="element-3188">
                      <Star className={`w-10 h-10 ${star <= rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'}`} data-id="element-3189" />
                    </button>)}
                </div>
              </div>
            </div>}
        </div>

        {/* Message Input - Fixed at Bottom */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-bottom z-20" data-id="element-3190">
          <div className="w-full mx-auto px-4 py-3" data-id="element-3191">
            <div className="flex items-center gap-2" data-id="element-3192">
              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-3193">
                <Paperclip className="w-5 h-5" data-id="element-3194" />
              </button>
              <input type="text" value={messageText} onChange={e => setMessageText(e.target.value)} onKeyPress={e => {
              if (e.key === 'Enter') {
                handleSendMessage();
              }
            }} placeholder="Type your message..." className="flex-1 px-4 py-2.5 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent" data-id="element-3195" />
              <button onClick={handleSendMessage} className="p-2.5 bg-secondary text-white rounded-full hover:bg-secondary-dark transition-colors" data-id="element-3196">
                <Send className="w-5 h-5" data-id="element-3197" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}